"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const price_format_query_resolver_1 = __importDefault(require("./price-format-query-resolver"));
const i18nPriceFormat_1 = require("@amzn/retail-i18n-price-format-typescript/dist/i18nPriceFormat");
const i18n_configuration_provider_1 = require("@amzn/i18n.configuration-provider");
const documents_json_1 = require("@amzn/retail-i18n-price-format-config/dist/acs_NewI18nPriceFormat_V2/documents.json");
const queries_json_1 = require("@amzn/retail-i18n-price-format-config/dist/acs_NewI18nPriceFormat_V2/queries.json");
const hierarchical_queries_json_1 = require("@amzn/retail-i18n-price-format-config/dist/acs_NewI18nPriceFormat_V2/hierarchical_queries.json");
const locale_fallback_resolver_1 = require("../utils/locale-fallback-resolver");
const price_format_args_sanitizer_1 = require("../utils/price-format-args-sanitizer");
/**
 * Resolves queries using the RetailI18nPriceFormat feature
 * (https://console.harmony.a2z.com/acs-web-editor/features/RetailI18nPriceFormat).
 */
class RetailPriceFormatQueryResolver extends price_format_query_resolver_1.default {
    resolve(attributeName, args) {
        super.validate(attributeName, args);
        let priceFormatArgs = price_format_args_sanitizer_1.PriceFormatArgsSanitizer.process(args);
        // go through the locale fallback chain until a match is found
        const fallbackLocales = locale_fallback_resolver_1.LocaleFallbackResolver.resolve(priceFormatArgs.locale);
        let config = new i18n_configuration_provider_1.I18nConfiguration.Builder().build();
        for (const locale of fallbackLocales) {
            priceFormatArgs = new i18n_configuration_provider_1.PriceFormatArgsBuilder()
                .fromPriceFormatArgs(priceFormatArgs)
                .withLocale(locale)
                .build();
            config = this.doResolve(attributeName, priceFormatArgs);
            if (!config.isEmpty()) {
                break;
            }
        }
        return config;
    }
    doResolve(attributeName, args) {
        const context = new i18nPriceFormat_1.I18nPriceFormatContext(args.cxFamily, args.cxProgram, super.createAcsContextInput(args.locale), super.createAcsContextInput(!(0, i18n_configuration_provider_1.isNullOrUndefined)(args.currency) ? args.currency.toLowerCase() : undefined), super.createAcsContextInput(args.cxId), super.createAcsContextInput(args.priceFormatType));
        const configBuilder = new i18n_configuration_provider_1.I18nConfiguration.Builder();
        switch (attributeName) {
            case i18n_configuration_provider_1.PriceFormat.CURRENCY_FORMAT:
                const customCurrency = this.getAcsClient().getCurrencyFormat(context);
                if (!(0, i18n_configuration_provider_1.isNullOrUndefined)(customCurrency)) {
                    configBuilder.addConfiguration(i18n_configuration_provider_1.PriceFormatResult.SYMBOL, customCurrency.symbol);
                    configBuilder.addConfiguration(i18n_configuration_provider_1.PriceFormatResult.INSERT_SPACE_IN_PATTERN, customCurrency.insertSpaceInPattern);
                    configBuilder.addConfiguration(i18n_configuration_provider_1.PriceFormatResult.FORMAT_PATTERN, customCurrency.formatPattern);
                }
                break;
            case i18n_configuration_provider_1.PriceFormat.SYMBOL_PREFIX:
                const symbolPrefix = this.getAcsClient().getSymbolPrefix(context);
                configBuilder.addConfiguration(i18n_configuration_provider_1.PriceFormatResult.SYMBOL_PREFIX, symbolPrefix);
                break;
            case i18n_configuration_provider_1.PriceFormat.CURRENCY_SYMBOL_POSITION:
                const currencySymbolPosition = this.getAcsClient().getCurrencySymbolPosition(context);
                configBuilder.addConfiguration(i18n_configuration_provider_1.PriceFormatResult.CURRENCY_SYMBOL_POSITION, this.convertSymbolPosition(currencySymbolPosition));
                break;
            case i18n_configuration_provider_1.PriceFormat.TRAILING_ZERO_DISPLAY:
                const trailingZeroDisplay = this.getAcsClient().getTrailingZeroDisplay(context);
                configBuilder.addConfiguration(i18n_configuration_provider_1.PriceFormatResult.TRAILING_ZERO_DISPLAY, this.convert(trailingZeroDisplay));
                break;
            default:
                throw new Error(`Attribute name ${attributeName} is not defined for feature ${attributeName.getConfigType()}`);
        }
        return configBuilder.build();
    }
    getAcsClient() {
        return super.acsClient;
    }
    convertSymbolPosition(symbolPosition) {
        if ((0, i18n_configuration_provider_1.isNullOrUndefined)(symbolPosition)) {
            return null;
        }
        const convertedSymbolPosition = RetailPriceFormatQueryResolver.CURRENCY_SYMBOL_POSITION_TABLE[symbolPosition];
        if ((0, i18n_configuration_provider_1.isNullOrUndefined)(convertedSymbolPosition)) {
            console.log(`Cannot convert ACS CurrencySymbolPosition ${symbolPosition} to configuration CurrencySymbolPosition.`);
            return null;
        }
        return convertedSymbolPosition;
    }
    convert(trailingZeroDisplay) {
        if ((0, i18n_configuration_provider_1.isNullOrUndefined)(trailingZeroDisplay)) {
            return null;
        }
        const convertedTrailingZeroDisplay = RetailPriceFormatQueryResolver.TRAILING_ZERO_DISPLAY_TABLE[trailingZeroDisplay];
        if ((0, i18n_configuration_provider_1.isNullOrUndefined)(convertedTrailingZeroDisplay)) {
            console.log(`Cannot convert ACS TralingZeroDisplay ${trailingZeroDisplay} to configuration TrailingZeroDisplay.`);
            return null;
        }
        return convertedTrailingZeroDisplay;
    }
    convertCustomCurrencyToString(customCurrency) {
        return `{
            "symbol": ${customCurrency.symbol},
            "insertSpaceInPattern": ${customCurrency.insertSpaceInPattern},
            "formatPattern": ${customCurrency.formatPattern}
        }`;
    }
    getSpecificArgsTargetConfigType() {
        return RetailPriceFormatQueryResolver.TARGET_CONFIG_TYPE;
    }
    getTypeSafeClientProvider() {
        return new i18nPriceFormat_1.I18nPriceFormatClientProvider();
    }
    getOfflineConfigFiles() {
        return {
            documents: documents_json_1.documents,
            exactQueries: queries_json_1.queries,
            hierarchicalFallbackQueries: hierarchical_queries_json_1.queries
        };
    }
    getValidationErrorMessage() {
        return `Retail Price Format configs can only be resolved for configType ${price_format_query_resolver_1.default.TARGET_CONFIG_TYPE} ` +
            `and args type ${RetailPriceFormatQueryResolver.TARGET_CONFIG_TYPE}`;
    }
}
exports.default = RetailPriceFormatQueryResolver;
RetailPriceFormatQueryResolver.TARGET_CONFIG_TYPE = i18n_configuration_provider_1.ConfigType.RETAIL_PRICE_FORMAT;
RetailPriceFormatQueryResolver.CURRENCY_SYMBOL_POSITION_TABLE = {
    [i18nPriceFormat_1.CurrencySymbolPosition.START]: i18n_configuration_provider_1.CurrencySymbolPosition.START,
    [i18nPriceFormat_1.CurrencySymbolPosition.END]: i18n_configuration_provider_1.CurrencySymbolPosition.END,
    [i18nPriceFormat_1.CurrencySymbolPosition.DEFAULT]: i18n_configuration_provider_1.CurrencySymbolPosition.DEFAULT,
};
RetailPriceFormatQueryResolver.TRAILING_ZERO_DISPLAY_TABLE = {
    [i18nPriceFormat_1.TrailingZeroDisplay.AUTO]: i18n_configuration_provider_1.TrailingZeroDisplay.AUTO,
    [i18nPriceFormat_1.TrailingZeroDisplay.HIDE_IF_WHOLE]: i18n_configuration_provider_1.TrailingZeroDisplay.HIDE_IF_WHOLE
};
//# sourceMappingURL=retail-price-format-query-resolver.js.map